/**
 * useContactMutations — W4.8
 *
 * Create / Update / Delete Contacts via TanStack useMutation.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type ContactFormData = {
  name: string
  email?: string | null
  phone?: string | null
  address?: string | null
  dashboard?: boolean
  monthly_report?: boolean
  broadcast_enabled?: boolean
  notes?: string | null
  helium_wallet?: string | null
  btc_wallet?: string | null
  eth_wallet?: string | null
  usdt_wallet?: string | null
  usdc_wallet?: string | null
  payment_method?: string | null
  currency?: string | null
  iban?: string | null
  paypal?: string | null
  tag_list?: string
}

// --- Create ---

export function useCreateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ContactFormData) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.POST('/api/v2/contacts', { body: { contact: data } })
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

// --- Update ---

export function useUpdateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number | string; data: ContactFormData }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.PATCH('/api/v2/contacts/{id}', {
        params: { path: { id: String(id) } },
        body: { contact: data },
      })
      // openapi-fetch setzt result.error auch bei Schema-Mismatch — ignoriere wenn response ok
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
      void queryClient.invalidateQueries({ queryKey: ['contact-detail', String(id)] })
    },
  })
}

// --- Delete ---

export function useDeleteContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number | string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.DELETE('/api/v2/contacts/{id}', {
        params: { path: { id: String(id) } },
      })
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}
